/**
 * IdentityFederationMapPageV2 — Stage 4-4.
 *
 * Visual graph of the org's federation peer relationships. Plain SVG
 * (no D3 dependency) — peers arranged in a ring around the central
 * "Org" node, edges colored by direction:
 *   - BIDIRECTIONAL (success green)
 *   - INBOUND_ONLY  (action blue)
 *   - OUTBOUND_ONLY (warning amber)
 * Edge dashing reflects bundle freshness (solid = fresh, dashed =
 * stale, double-dashed = never refreshed / danger).
 *
 * Empty-state when no peers exist — directs the user to the
 * federation-peers management page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Network, Plus, AlertTriangle, ArrowRight } from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { EmptyState } from '../components/v2/EmptyState';
import { Skeleton } from '../components/v2/Skeleton';
import { listIdentityFederationPeers } from '../services/api';

const DIRECTION_COLORS = {
  BIDIRECTIONAL: '#16a34a', // success green
  INBOUND_ONLY:  '#2563eb', // action blue
  OUTBOUND_ONLY: '#d97706', // warning amber
};

function bundleFreshness(iso) {
  if (!iso) return 'never';   // dashed-double, danger color
  const minutesAgo = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutesAgo < 60)   return 'fresh';   // solid
  if (minutesAgo < 1440) return 'stale';   // dashed
  return 'expired';                         // dashed-double
}

export default function IdentityFederationMapPageV2() {
  const navigate = useNavigate();
  const [peers, setPeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    listIdentityFederationPeers({ includeRetired: false }).then((res) => {
      if (res?.ok === false) {
        setError(res.error || 'Could not load federation peers.');
        setPeers([]);
      } else {
        setPeers(Array.isArray(res?.data) ? res.data : []);
      }
      setLoading(false);
    });
  }, []);

  // Ring layout — peers arranged around the central org node.
  // Memoised on peers so SVG positions don't reshuffle on hover.
  const layout = useMemo(() => {
    const cx = 360;
    const cy = 280;
    const radius = 200;
    const n = peers.length;
    return peers.map((p, i) => {
      const theta = (2 * Math.PI * i) / Math.max(n, 1) - Math.PI / 2; // start at top
      return {
        peer: p,
        x: cx + radius * Math.cos(theta),
        y: cy + radius * Math.sin(theta),
        cx, cy,
      };
    });
  }, [peers]);

  return (
    <V2PageShell
      title="Federation map"
      subtitle="Visual graph of federation peer relationships in this organization."
      backTo="/identity"
      backLabel="Back to Identity"
      tags={
        <>
          <Pill tone="trust"  size="sm">Identity</Pill>
          <Pill tone="action" size="sm" dot>SPIFFE plane</Pill>
        </>
      }
      actions={
        <Button variant="primary" size="sm" iconLeft={<Plus size={14} />}
                onClick={() => navigate('/identity/federation-peers')}>
          Manage peers
        </Button>
      }
    >
      {loading && (
        <Card><Skeleton height={500} /></Card>
      )}

      {!loading && error && (
        <Card>
          <EmptyState
            icon={<AlertTriangle />}
            title="Could not load federation map"
            description={error}
            primaryAction={
              <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
                Retry
              </Button>
            }
          />
        </Card>
      )}

      {!loading && !error && peers.length === 0 && (
        <Card>
          <EmptyState
            icon={<Network />}
            title="No federation peers yet"
            description="Register your first federation peer to see the trust-domain map render here."
            primaryAction={
              <Button variant="primary" size="sm" iconLeft={<Plus size={14} />}
                      onClick={() => navigate('/identity/federation-peers')}>
                Register first peer
              </Button>
            }
          />
        </Card>
      )}

      {!loading && !error && peers.length > 0 && (
        <>
          {/* Legend */}
          <Card padding="sm" className="mb-3">
            <div className="flex items-center gap-4 text-[11px]">
              <span className="text-v2-ink-muted">Direction:</span>
              <span className="flex items-center gap-1.5">
                <span style={{ width: 18, height: 2, background: DIRECTION_COLORS.BIDIRECTIONAL, display: 'inline-block' }} />
                Bidirectional
              </span>
              <span className="flex items-center gap-1.5">
                <span style={{ width: 18, height: 2, background: DIRECTION_COLORS.INBOUND_ONLY, display: 'inline-block' }} />
                Inbound only
              </span>
              <span className="flex items-center gap-1.5">
                <span style={{ width: 18, height: 2, background: DIRECTION_COLORS.OUTBOUND_ONLY, display: 'inline-block' }} />
                Outbound only
              </span>
              <span className="ml-3 text-v2-ink-muted">Bundle:</span>
              <span className="flex items-center gap-1.5">
                <span style={{ width: 18, height: 2, background: '#888', display: 'inline-block' }} />
                Fresh
              </span>
              <span className="flex items-center gap-1.5">
                <span style={{ width: 18, height: 2, background: '#888', borderTop: '2px dashed #888', display: 'inline-block' }} />
                Stale
              </span>
              <span className="flex items-center gap-1.5 text-v2-danger">
                <ArrowRight size={11} aria-hidden="true" />
                Expired
              </span>
            </div>
          </Card>

          <Card padding="md">
            <svg
              viewBox="0 0 720 560"
              style={{ width: '100%', height: 'auto', display: 'block' }}
              role="img"
              aria-label="Federation peer map">
              {/* Edges first so nodes draw on top */}
              {layout.map(({ peer, x, y, cx, cy }) => {
                const color = DIRECTION_COLORS[peer.direction] || '#888';
                const freshness = bundleFreshness(peer.bundleLastRefreshedAt);
                const strokeDasharray =
                  freshness === 'fresh'   ? null :
                  freshness === 'stale'   ? '6 4' :
                  '2 4 2 8';
                return (
                  <g key={`edge-${peer.id}`}>
                    <line
                      x1={cx} y1={cy} x2={x} y2={y}
                      stroke={color}
                      strokeWidth={2}
                      strokeDasharray={strokeDasharray || undefined}
                      opacity={freshness === 'never' ? 0.5 : 0.85}
                    />
                  </g>
                );
              })}

              {/* Central org node */}
              <g>
                <circle cx={360} cy={280} r={56}
                        fill="var(--v2-action-soft, #DBEAFE)"
                        stroke="var(--v2-action, #2563eb)" strokeWidth={2} />
                <text x={360} y={276}
                      textAnchor="middle"
                      fontSize="13"
                      fontWeight={600}
                      fill="var(--v2-ink, #111)">
                  Organization
                </text>
                <text x={360} y={294}
                      textAnchor="middle"
                      fontSize="11"
                      fill="var(--v2-ink-secondary, #555)">
                  {peers.length} {peers.length === 1 ? 'peer' : 'peers'}
                </text>
              </g>

              {/* Peer nodes */}
              {layout.map(({ peer, x, y }) => {
                const color = DIRECTION_COLORS[peer.direction] || '#888';
                return (
                  <g key={`node-${peer.id}`}
                     onClick={() => navigate('/identity/federation-peers')}
                     style={{ cursor: 'pointer' }}>
                    <circle cx={x} cy={y} r={36}
                            fill="var(--v2-surface, #fff)"
                            stroke={color} strokeWidth={2} />
                    <text x={x} y={y - 2}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight={600}
                          fill="var(--v2-ink, #111)">
                      {(peer.name || 'Peer').length > 14
                        ? (peer.name || 'Peer').substring(0, 13) + '…'
                        : (peer.name || 'Peer')}
                    </text>
                    <text x={x} y={y + 14}
                          textAnchor="middle"
                          fontSize="9"
                          fill="var(--v2-ink-muted, #888)">
                      {peer.direction}
                    </text>
                  </g>
                );
              })}
            </svg>
          </Card>

          {/* Per-peer details row beneath the SVG so screen readers
              and copy/paste users get the table view too. */}
          <Card padding="sm" className="mt-3">
            <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-2">
              Peer details
            </div>
            <div className="flex flex-col gap-1.5">
              {peers.map((p) => (
                <div key={p.id} className="flex items-center gap-3 text-[12px]">
                  <span className="inline-block w-2 h-2 rounded-full"
                        style={{ background: DIRECTION_COLORS[p.direction] || '#888' }} />
                  <span className="font-semibold text-v2-ink min-w-[120px]">{p.name}</span>
                  <span className="font-mono-v2 text-v2-ink-muted truncate flex-1">{p.spiffeId}</span>
                  <span className="text-v2-ink-muted">{p.direction}</span>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </V2PageShell>
  );
}
